import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Pill } from '../../../shared/Pill';
import { Pagination, PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize } from '../../../shared/pagination';
import { formatDateTime } from '../../../shared/tenantClock';
import { formatNumber } from '../../../shared/numberFormat';
import { useErrorToast } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';

// QuickBooks Activity (issue #265): every exchange with QuickBooks, newest
// first: Item, Customer, tax code and Term fetches, and Customers created or updated in
// QuickBooks; Invoice pushes, stock reads and so on join as their stories
// land.

const KIND_LABELS = {
  item_fetch: 'Items fetched',
  customer_fetch: 'Customers fetched',
  customer_create: 'Customer created',
  customer_update: 'Customer updated',
  tax_code_fetch: 'Tax codes fetched',
  term_fetch: 'Terms fetched',
  invoice_copy: 'Invoice copied',
  invoice_void: 'Invoice voided',
};

const DIRECTION_LABELS = {
  inbound: 'From QuickBooks',
  outbound: 'To QuickBooks',
};

const TH = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const TD = { padding: '8px', fontSize: 14 };

function QuickBooksActivityTab() {
  const showError = useErrorToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  useDefaultPageSize(setLimit);

  useEffect(() => {
    setLoading(true);
    axios.get('/api/quickbooks/activity', { params: { page, limit } })
      .then((res) => {
        setRows(res.data.data || []);
        setPagination(res.data.pagination || { page, pages: 1, total: 0 });
      })
      .catch((err) => showError(err, 'Failed to load QuickBooks Activity'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [page, limit]);

  return (
    <>
      <div style={{ margin: '16px 24px 0', fontSize: 13, color: '#666' }}>
        Everything Optim has read from or sent to QuickBooks.
      </div>
      <div style={{ margin: '16px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={TH}>When</th>
              <th style={TH}>Direction</th>
              <th style={TH}>What</th>
              <th style={TH}>Outcome</th>
              <th style={TH}>Details</th>
              <th style={{ ...TH, textAlign: 'right' }}>Records</th>
              <th style={TH}>By</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Nothing has been exchanged with QuickBooks yet</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} className="list-row">
                <td data-label="When" style={{ ...TD, whiteSpace: 'nowrap' }}>{formatDateTime(row.created_at)}</td>
                <td data-label="Direction" style={TD}>{DIRECTION_LABELS[row.direction] || row.direction}</td>
                <td data-label="What" style={TD}>{KIND_LABELS[row.kind] || row.kind}</td>
                <td data-label="Outcome" style={TD}>
                  {row.outcome === 'success'
                    ? <Pill color="#1a7f4b" background="rgba(26, 127, 75, 0.12)">Succeeded</Pill>
                    : <Pill color="#b91c1c" background="#fef2f2">Failed</Pill>}
                </td>
                <td data-label="Details" style={{ ...TD, fontSize: 13, color: '#555' }}>{row.summary}</td>
                <td data-label="Records" style={{ ...TD, textAlign: 'right' }}>{row.record_count == null ? '' : formatNumber(row.record_count)}</td>
                <td data-label="By" style={TD}>{row.user_name || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Entries"
        rightContent={(
          <select
            className="styled-input"
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
            style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}
          >
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </>
  );
}

export default QuickBooksActivityTab;
